import { initializeApp } from 'firebase/app';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  signOut as fbSignOut,
  getAdditionalUserInfo,
  deleteUser,
  User,
  Unsubscribe,
} from 'firebase/auth';
import { firebaseConfig } from './config';
import type { FirebaseOptions } from 'firebase/app';
import { postSignupEvent, collectBrowserContext } from '../services/authTelemetryClient';

const authLog = (op: string, detail: Record<string, unknown> = {}) => {
  // Lightweight client-side trace. Console only - telemetry endpoint is
  // called explicitly from signup paths via postSignupEvent below.
  // `detail` frequently carries PII (uid), so gate to non-production to keep
  // identifiers out of shipped browser consoles (security finding D.7).
  if (process.env.NODE_ENV === 'production') return;
  // eslint-disable-next-line no-console
  console.info(`[auth] ${op}`, detail);
};

/**
 * Best-effort Firebase ID token for telemetry auth (security finding C.6).
 * Never throws - resolves to null on any failure so signup telemetry stays
 * fire-and-forget. The backend uses the token to re-derive identity instead
 * of trusting the client-supplied uid/email on SUCCESS events.
 */
const safeIdToken = async (fbUser: User | null): Promise<string | null> => {
  if (!fbUser) return null;
  try {
    return await fbUser.getIdToken();
  } catch {
    return null;
  }
};

/**
 * Where Firebase should redirect after the user clicks the email link.
 * Matches the "Action URL" set in Firebase Console > Authentication > Templates
 * (https://stockbai.com/auth/action-complete). Combined with the console
 * template change, this delivers users back to AuthActionCompletePage.tsx
 * instead of the generic firebaseapp.com hosted page.
 *
 * REACT_APP_PUBLIC_URL overrides for non-prod environments; default safe for
 * stockbai.com prod build.
 */
const APP_PUBLIC_URL = (
  process.env.REACT_APP_PUBLIC_URL || 'https://stockbai.com'
).replace(/\/+$/, '');

const verifyActionSettings = (): { url: string; handleCodeInApp: boolean } => ({
  url: `${APP_PUBLIC_URL}/auth/action-complete?mode=verifyEmail`,
  handleCodeInApp: false,
});

const resetActionSettings = (): { url: string; handleCodeInApp: boolean } => ({
  url: `${APP_PUBLIC_URL}/auth/action-complete?mode=resetPassword`,
  handleCodeInApp: false,
});

export interface UserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
}

export interface AuthErrorInfo {
  code?: string;
  message: string;
}

// When Firebase env vars are missing (e.g. old deploy or wrong build), we must not call
// initializeApp() - Firebase throws auth/invalid-api-key and crashes the app (black screen).
const isFirebaseConfigured = (): boolean =>
  !!firebaseConfig.apiKey && !!firebaseConfig.projectId;

function toUserProfile(user: User | null): UserProfile | null {
  if (!user) return null;
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    emailVerified: user.emailVerified,
  };
}

const noopUnsubscribe = (): void => {};

interface IAuthService {
  isAvailable(): boolean;
  onAuthStateChanged(callback: (user: UserProfile | null) => void): Unsubscribe;
  getCurrentUser(): UserProfile | null;
  getIdToken(forceRefresh?: boolean): Promise<string | null>;
  getRoles(): Promise<string[]>;
  signInWithEmail(email: string, password: string): Promise<UserProfile>;
  registerWithEmail(
    email: string,
    password: string
  ): Promise<{ user: UserProfile; verificationSent: boolean }>;
  sendVerificationEmail(): Promise<void>;
  sendPasswordReset(email: string): Promise<void>;
  signInWithGoogle(): Promise<UserProfile>;
  registerWithGoogle(): Promise<UserProfile>;
  signOut(): Promise<void>;
}

const stubAuthService: IAuthService = {
  isAvailable: () => false,
  onAuthStateChanged: (cb: (user: UserProfile | null) => void): Unsubscribe => {
    cb(null);
    return noopUnsubscribe;
  },
  getCurrentUser: () => null,
  getIdToken: async () => null,
  getRoles: async () => [],
  signInWithEmail: async () => {
    throw new Error('Auth is not configured');
  },
  registerWithEmail: async () => {
    throw new Error('Auth is not configured');
  },
  sendVerificationEmail: async () => {
    throw new Error('Auth is not configured');
  },
  sendPasswordReset: async () => {
    throw new Error('Auth is not configured');
  },
  signInWithGoogle: async () => {
    throw new Error('Auth is not configured');
  },
  registerWithGoogle: async () => {
    throw new Error('Auth is not configured');
  },
  signOut: async () => {},
};

let authService: IAuthService;
let auth: ReturnType<typeof getAuth> | null;

if (isFirebaseConfigured()) {
  const firebaseOptions: FirebaseOptions = {
    apiKey: firebaseConfig.apiKey,
    authDomain: firebaseConfig.authDomain,
    projectId: firebaseConfig.projectId,
    storageBucket: firebaseConfig.storageBucket,
    messagingSenderId: firebaseConfig.messagingSenderId,
    appId: firebaseConfig.appId,
    ...(firebaseConfig.measurementId != null && firebaseConfig.measurementId !== ''
      ? { measurementId: firebaseConfig.measurementId }
      : {}),
  };

  const app = initializeApp(firebaseOptions);
  auth = getAuth(app);

  class AuthService {
    private currentUser: User | null = null;
    private initialized = false;
    private provider = new GoogleAuthProvider();

    constructor() {
      try {
        onAuthStateChanged(auth!, (user) => {
          this.currentUser = user;
          this.initialized = true;
        });
      } catch {
        this.initialized = true;
      }
    }

    isAvailable(): boolean {
      return true;
    }

    onAuthStateChanged(callback: (user: UserProfile | null) => void): Unsubscribe {
      return onAuthStateChanged(auth!, (user) => callback(toUserProfile(user)));
    }

    getCurrentUser(): UserProfile | null {
      return toUserProfile(this.currentUser);
    }

    async getIdToken(forceRefresh = false): Promise<string | null> {
      const user = auth!.currentUser;
      if (!user) return null;
      try {
        return await user.getIdToken(forceRefresh);
      } catch {
        return null;
      }
    }

    async getRoles(): Promise<string[]> {
      const user = auth!.currentUser;
      if (!user) return [];
      try {
        // Force refresh so custom claims updated server-side (grantRole, Console, etc.) are visible.
        const res = await user.getIdTokenResult(true);
        const claims = (res?.claims || {}) as Record<string, unknown>;
        const out: string[] = [];

        const push = (r: unknown) => {
          if (typeof r === 'string' && r.trim()) out.push(r.trim().toLowerCase());
        };

        if (Array.isArray(claims.roles)) {
          for (const r of claims.roles) push(r);
        } else if (typeof claims.roles === 'string' && claims.roles.trim()) {
          try {
            const parsed = JSON.parse(claims.roles) as unknown;
            if (Array.isArray(parsed)) {
              for (const r of parsed) push(r);
            } else {
              push(claims.roles);
            }
          } catch {
            push(claims.roles);
          }
        }

        if (typeof claims.role === 'string') push(claims.role);

        // Some setups set only a boolean (e.g. Firebase Console or legacy scripts).
        if (claims.admin === true) out.push('admin');

        return [...new Set(out)];
      } catch {
        return [];
      }
    }

    async signInWithEmail(email: string, password: string): Promise<UserProfile> {
      authLog('signInWithEmail:start');
      try {
        const cred = await signInWithEmailAndPassword(auth!, email, password);
        authLog('signInWithEmail:ok', { uid: cred.user.uid });
        return toUserProfile(cred.user)!;
      } catch (err: any) {
        authLog('signInWithEmail:fail', { code: err?.code });
        throw err;
      }
    }

    async registerWithEmail(
      email: string,
      password: string
    ): Promise<{ user: UserProfile; verificationSent: boolean }> {
      authLog('registerWithEmail:start');
      let cred;
      try {
        cred = await createUserWithEmailAndPassword(auth!, email, password);
      } catch (err: any) {
        authLog('registerWithEmail:fail', { code: err?.code });
        // Fire telemetry: account never created. firebase_uid absent.
        postSignupEvent({
          email,
          provider: 'email',
          verification_sent: null,
          error_code: err?.code ?? 'unknown',
          error_message: err?.message ?? null,
          ...collectBrowserContext(),
        });
        throw err;
      }

      let verificationSent = true;
      let verificationErrorCode: string | null = null;
      let verificationErrorMessage: string | null = null;
      try {
        await sendEmailVerification(cred.user, verifyActionSettings());
        authLog('sendEmailVerification:ok', { uid: cred.user.uid });
      } catch (e: any) {
        console.warn('Could not send verification email:', e);
        verificationSent = false;
        verificationErrorCode = e?.code ?? 'unknown';
        verificationErrorMessage = e?.message ?? null;
        authLog('sendEmailVerification:fail', { code: verificationErrorCode });
      }

      // SUCCESS event: user is authenticated, so attach a Bearer token (best
      // effort, non-blocking) for backend identity re-derivation (C.6).
      safeIdToken(cred.user).then((token) =>
        postSignupEvent(
          {
            firebase_uid: cred.user.uid,
            email,
            provider: 'email',
            verification_sent: verificationSent,
            error_code: verificationErrorCode,
            error_message: verificationErrorMessage,
            display_name: cred.user.displayName,
            photo_url: cred.user.photoURL,
            ...collectBrowserContext(),
          },
          token
        )
      );

      return { user: toUserProfile(cred.user)!, verificationSent };
    }

    async sendVerificationEmail(): Promise<void> {
      const user = auth!.currentUser;
      if (!user) throw new Error('No user signed in');
      if (user.emailVerified) throw new Error('Email is already verified');
      authLog('resendVerificationEmail:start', { uid: user.uid });
      try {
        await sendEmailVerification(user, verifyActionSettings());
        authLog('resendVerificationEmail:ok');
      } catch (err: any) {
        authLog('resendVerificationEmail:fail', { code: err?.code });
        // Telemetry: resend failure also of interest to admin (correlates
        // with EmailVerificationPage "Resend" button activity). The user IS
        // authenticated here (resend targets the current user), so attach a
        // Bearer token for backend identity re-derivation (C.6).
        safeIdToken(user).then((token) =>
          postSignupEvent(
            {
              firebase_uid: user.uid,
              email: user.email,
              provider: 'email',
              verification_sent: false,
              error_code: err?.code ?? 'unknown',
              error_message: err?.message ?? null,
              display_name: user.displayName,
              photo_url: user.photoURL,
              ...collectBrowserContext(),
            },
            token
          )
        );
        throw err;
      }
    }

    async sendPasswordReset(email: string): Promise<void> {
      authLog('sendPasswordReset:start');
      try {
        await sendPasswordResetEmail(auth!, email, resetActionSettings());
        authLog('sendPasswordReset:ok');
      } catch (err: any) {
        authLog('sendPasswordReset:fail', { code: err?.code });
        throw err;
      }
    }

    async signInWithGoogle(): Promise<UserProfile> {
      authLog('signInWithGoogle:start');
      const cred = await signInWithPopup(auth!, this.provider);
      const isNewUser = getAdditionalUserInfo(cred)?.isNewUser ?? false;
      if (isNewUser) {
        authLog('signInWithGoogle:new-user-blocked', { uid: cred.user.uid });
        try {
          await deleteUser(cred.user);
        } catch (deleteErr) {
          // FAIL CLOSED (security finding D.8): the just-created Firebase
          // account still exists. If we returned the soft
          // "auth/new-user-must-register" here, a retry could let the user in
          // without ever accepting the Disclaimer (the account is no longer
          // "new"). Force a sign-out and surface a HARD, distinct error so the
          // caller does NOT treat this as the normal "go register" path.
          console.error(
            '⚠️ Failed to delete just-created Firebase user; disclaimer-bypass risk on retry',
            deleteErr
          );
          try {
            await fbSignOut(auth!);
          } catch (signOutErr) {
            console.error('⚠️ Sign-out after failed cleanup also failed', signOutErr);
          }
          const hardErr: any = new Error(
            'Could not complete sign-in. Please try again, and if it persists contact support.'
          );
          hardErr.code = 'auth/new-user-cleanup-failed';
          throw hardErr;
        }
        // Happy path: account successfully removed; block with the soft
        // "must register first" signal the UI already understands.
        try { await fbSignOut(auth!); } catch { /* best-effort */ }
        const err: any = new Error('New users must sign up first and agree to the Disclaimer.');
        err.code = 'auth/new-user-must-register';
        throw err;
      }
      authLog('signInWithGoogle:ok', { uid: cred.user.uid });
      return toUserProfile(cred.user)!;
    }

    async registerWithGoogle(): Promise<UserProfile> {
      authLog('registerWithGoogle:start');
      try {
        const cred = await signInWithPopup(auth!, this.provider);
        authLog('registerWithGoogle:ok', { uid: cred.user.uid });
        // No verification email is sent for Google OAuth - email is already
        // verified at Google. Store null so admin UI doesn't show a misleading
        // "verification sent" tick for a step that never happened.
        // SUCCESS event: attach a Bearer token (best effort) for backend
        // identity re-derivation (C.6).
        safeIdToken(cred.user).then((token) =>
          postSignupEvent(
            {
              firebase_uid: cred.user.uid,
              email: cred.user.email,
              provider: 'google',
              verification_sent: null,
              display_name: cred.user.displayName,
              photo_url: cred.user.photoURL,
              ...collectBrowserContext(),
            },
            token
          )
        );
        return toUserProfile(cred.user)!;
      } catch (err: any) {
        authLog('registerWithGoogle:fail', { code: err?.code });
        // Quiet user-cancelled-popup. Still log other Google failures.
        if (err?.code !== 'auth/popup-closed-by-user' && err?.code !== 'auth/cancelled-popup-request') {
          postSignupEvent({
            provider: 'google',
            verification_sent: null,
            error_code: err?.code ?? 'unknown',
            error_message: err?.message ?? null,
            ...collectBrowserContext(),
          });
        }
        throw err;
      }
    }

    async signOut(): Promise<void> {
      authLog('signOut');
      await fbSignOut(auth!);
    }
  }

  authService = new AuthService();
} else {
  auth = null;
  authService = stubAuthService;
}

export { authService, auth };
